<template>
  <o-table
    sticky-header
    :items="tasks"
    :loading="loading"
    :draggable="false"
    :default-sort="['updatedAt', 'desc']"
  >
    <b-table-column v-slot="{ row }" field="status" label="Statut" sortable>
      <div class="is-flex is-align-items-center">
        <b-icon
          :icon="row.icon"
          :class="{ 'fa-spin': row.started }"
          :type="row.type"
          size="is-small"
          class="mr-1"
        />
        <b-tag :type="row.type">
          {{ row.tag }}
        </b-tag>
      </div>
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      field="subscribedTrackerNumber"
      label="Abonné"
      centered
      sortable
    >
      {{ row.subscribedTrackerNumber }}
    </b-table-column>
    <b-table-column centered>
      <b-icon icon="angle-double-right" />
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      field="unsubscribedTrackerNumber"
      label="Désabonné"
      centered
      sortable
    >
      {{ row.unsubscribedTrackerNumber }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="updatedAt" label="Mise à jour" sortable>
      {{ formatDate(row.updatedAt) }}
    </b-table-column>

    <template v-if="canUpdateStatus">
      <b-table-column v-slot="{ row }" width="24" centered>
        <div class="is-right">
          <b-tooltip v-if="showRetryButton(row)" position="is-left" multilined type="is-light">
            <b-button type="is-primary" class="is-small" @click="retryTask(row)">
              <b-icon icon="undo" />
            </b-button>
            <template v-slot:content> Relancer le transfert </template>
          </b-tooltip>
        </div>
      </b-table-column>
      <b-table-column v-slot="{ row }" width="24" centered>
        <div class="is-right">
          <b-tooltip v-if="showAbortButton(row)" position="is-left" multilined type="is-light">
            <b-button type="is-warning" class="is-small" @click="abortTask(row)">
              <b-icon icon="ban" />
            </b-button>
            <template v-slot:content> Annuler le transfert </template>
          </b-tooltip>
        </div>
      </b-table-column>
    </template>
  </o-table>
</template>

<script>
import OTable from '@components/Table.vue'
import { mapActions } from 'vuex'
import { format } from 'date-fns'

const SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS = {
  PENDING: 'pending',
  STARTED: 'started',
  SUCCEEDED: 'succeeded',
  ABORTED: 'aborted',
  TRACKER_NOT_FOUND: 'tracker-not-found',
  TRACKER_UNAVAILABLE: 'tracker-unavailable',
  TRACKER_ALREADY_SUBSCRIBED: 'tracker-already-subscribed',
  FETCH_DEVICE_DETAIL_FAILED: 'fetch-device-detail-failed',
  SWAP_SUBSCRIPTION_FAILED: 'swap-subscription-failed',
  UPDATE_DEVICE_NAME_FAILED: 'update-device-name-failed',
  UNEXPECTED_FAILED: 'unexpected-failed',
}

export const SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS_CONFIG = {
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.PENDING]: {
    tag: 'En attente',
    type: 'is-primary',
    icon: 'hourglass-end',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.STARTED]: {
    tag: 'En cours',
    type: 'is-info',
    icon: 'spinner',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.SUCCEEDED]: {
    tag: 'Transféré',
    type: 'is-success',
    icon: 'check',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.ABORTED]: {
    tag: 'Annulé',
    type: 'is-warning',
    icon: 'ban',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.TRACKER_NOT_FOUND]: {
    tag: 'Tracker introuvable',
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.TRACKER_UNAVAILABLE]: {
    tag: 'Tracker indisponible',
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.TRACKER_ALREADY_SUBSCRIBED]: {
    tag: 'Tracker déjà abonné',
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.FETCH_DEVICE_DETAIL_FAILED]: {
    tag: 'Échec de la récupération des informations Findmespot',
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.SWAP_SUBSCRIPTION_FAILED]: {
    tag: "Échec du transfert d'abonnement Findmespot",
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.UPDATE_DEVICE_NAME_FAILED]: {
    tag: 'Échec du renommage du tracker Findmespot',
    type: 'is-danger',
    icon: 'times',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.UNEXPECTED_FAILED]: {
    tag: 'Erreur inattendue',
    type: 'is-danger',
    icon: 'times',
  },
}

const LOAD_INTERVAL_IN_SECONDS = 5

export default {
  name: 'SpotTrackerSubscriptionTaskTable',

  components: {
    OTable,
  },

  props: {
    disableStatusUpdate: {
      type: Boolean,
      required: true,
    },
    spotTrackers: {
      type: Object,
      required: true,
    },
    spotTrackerSubscriptionTaskGroup: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      loadIntervalId: null,
      spotTrackerSubscriptionTasks: [],
    }
  },

  async mounted() {
    this.loading = true
    await this.fetchSpotTrackerSubscriptionTasks()
    this.loading = false
  },

  beforeDestroy() {
    this.stopLoadPolling()
  },

  watch: {
    tasksCompleted: {
      handler(tasksCompleted) {
        this.$emit('completed', tasksCompleted)
      },
      immediate: true,
    },

    disableStatusUpdate: {
      handler(disableStatusUpdate) {
        if (disableStatusUpdate) {
          this.stopLoadPolling()

          return
        }

        this.startLoadPolling()
      },
      immediate: true,
    },
  },

  computed: {
    tasks() {
      return this.spotTrackerSubscriptionTasks.map((task) => {
        const config = SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS_CONFIG[task.status] ?? {
          tag: task.status,
          type: 'is-light',
          icon: 'question',
        }

        return {
          ...task,
          ...config,
          started: task.status === SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.STARTED,
          aborted: task.status === SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.ABORTED,
          succeeded: task.status === SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.SUCCEEDED,
          subscribedTrackerNumber: this.spotTrackers[task.subscribedTrackerId],
          unsubscribedTrackerNumber: this.spotTrackers[task.unsubscribedTrackerId],
        }
      })
    },

    canUpdateStatus() {
      return !this.disableStatusUpdate && this.tasks.some((task) => task.isFailed || task.aborted)
    },

    tasksCompleted() {
      return this.tasks.length > 0 && this.tasks.every((task) => task.succeeded || task.aborted)
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    formatDate(date) {
      return format(date, 'dd/MM/yyyy HH:mm:ss')
    },

    showRetryButton(task) {
      return !this.disableStatusUpdate && (task.isFailed || task.aborted)
    },

    showAbortButton(task) {
      return !this.disableStatusUpdate && task.isFailed
    },

    startLoadPolling() {
      this.stopLoadPolling()

      this.loadIntervalId = window.setInterval(async () => {
        await this.fetchSpotTrackerSubscriptionTasks()
      }, LOAD_INTERVAL_IN_SECONDS * 1000)
    },

    stopLoadPolling() {
      if (this.loadIntervalId) {
        window.clearInterval(this.loadIntervalId)
      }
    },

    async fetchSpotTrackerSubscriptionTasks() {
      try {
        this.spotTrackerSubscriptionTasks =
          await this.$services.spotTrackerSubscriptionTaskService.getAllByGroupId(
            this.spotTrackerSubscriptionTaskGroup.id,
          )
      } catch (err) {
        this.spotTrackerSubscriptionTasks = []
        console.error(err)
      }
    },

    async retryTask(task) {
      this.loading = true

      this.stopLoadPolling()

      try {
        const retriedTask = await this.$services.spotTrackerSubscriptionTaskService.update(
          task.id,
          SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.PENDING,
        )

        this.spotTrackerSubscriptionTasks = this.spotTrackerSubscriptionTasks.map((t) =>
          t.id === retriedTask.id ? retriedTask : t,
        )

        this.addToastMessage({
          text: `Le transfert a été relancé.`,
          type: 'is-success',
        })
      } catch (err) {
        this.addToastMessage({
          text: err.message ?? `Impossible de relancer le transfert.`,
          type: 'is-danger',
        })
        console.error(err)
      }

      this.startLoadPolling()

      this.loading = false
    },

    async abortTask(task) {
      this.loading = true

      this.stopLoadPolling()

      try {
        const abortedTask = await this.$services.spotTrackerSubscriptionTaskService.update(
          task.id,
          SPOT_TRACKER_SUBSCRIPTION_TASK_STATUS.ABORTED,
        )

        this.spotTrackerSubscriptionTasks = this.spotTrackerSubscriptionTasks.map((t) =>
          t.id === abortedTask.id ? abortedTask : t,
        )

        this.addToastMessage({
          text: `Le transfert a été annulé.`,
          type: 'is-success',
        })
      } catch (err) {
        this.addToastMessage({
          text: err.message ?? `Impossible d'annuler le transfert.`,
          type: 'is-danger',
        })
        console.error(err)
      }

      this.startLoadPolling()

      this.loading = false
    },
  },
}
</script>
